import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Banknote,
  ChevronRight,
  Clock,
  HeartHandshake,
  Lock,
  Shield,
  Smartphone,
  Star,
  TrendingUp,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";

const stats = [
  { value: "K1,000,000", label: "Max loan amount" },
  { value: "24hrs", label: "Approval turnaround" },
  { value: "15%", label: "Starting interest rate" },
  { value: "24 mo", label: "Flexible terms" },
];

const steps = [
  { icon: Shield, title: "Verify identity", desc: "Upload your NRC once" },
  { icon: Banknote, title: "Choose amount", desc: "See costs upfront" },
  { icon: Zap, title: "Get funded", desc: "Bank or mobile money" },
];

const highlights = [
  { icon: Clock, text: "24-hour decisions" },
  { icon: TrendingUp, text: "No hidden charges" },
  { icon: Smartphone, text: "Mobile money payouts" },
  { icon: HeartHandshake, text: "No early repayment fees" },
  { icon: Lock, text: "Bank-level encryption" },
];

const testimonials = [
  {
    name: "Mwila K.",
    location: "Lusaka",
    text: "I applied in the morning and had my money by end of day.",
    rating: 5,
  },
  {
    name: "Chanda M.",
    location: "Ndola",
    text: "I could see exactly what I'd pay each month before committing.",
    rating: 5,
  },
  {
    name: "Bwalya S.",
    location: "Kitwe",
    text: "Everything was done from my phone. No bank trips needed.",
    rating: 5,
  },
];

const Index = () => {
  return (
    <div className="container mx-auto px-6 pb-20">
      {/* ── Hero ── */}
      <section className="mx-auto max-w-4xl pt-20 pb-16 text-center lg:pt-28">
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-semibold text-emerald-700 mb-6">
          <Star className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500" />
          Trusted by borrowers across Zambia
        </div>

        <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-[3.5rem] font-display">
          Get the cash you need,
          <br />
          <span className="bg-gradient-to-r from-primary via-primary to-emerald-600 bg-clip-text text-transparent">
            when you need it.
          </span>
        </h1>

        <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground leading-relaxed">
          Borrow from K10,000 to K1,000,000 in Zambian Kwacha. See your costs
          before you commit. Get funded to your bank or mobile money.
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="h-13 px-8 text-base rounded-xl shadow-lg shadow-primary/20">
            <Link to="/register">
              Apply now — it's free
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="h-13 px-8 text-base rounded-xl"
          >
            <Link to="/login">Sign in to your account</Link>
          </Button>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          No impact on your credit score to check eligibility
        </p>
      </section>

      {/* ── Trust stats ── */}
      <section className="mx-auto max-w-3xl">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-border/60 bg-white/80 p-5 text-center shadow-sm backdrop-blur"
            >
              <p className="text-2xl font-bold text-foreground font-display">
                {s.value}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works (preview) ── */}
      <section className="mx-auto max-w-4xl pt-24">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            How it works
          </p>
          <h2 className="mt-3 text-3xl font-bold text-foreground font-display sm:text-4xl">
            Three steps. That's it.
          </h2>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <div
              key={s.title}
              className="group relative rounded-2xl border border-border/50 bg-white p-7 shadow-sm transition-all hover:shadow-md hover:border-primary/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                  <s.icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold text-muted-foreground/50 uppercase tracking-wider">
                  Step {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-foreground">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {s.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 text-center">
          <Button asChild variant="ghost" className="text-primary">
            <Link to="/how-it-works">
              See the full process
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ── Why us (preview) ── */}
      <section className="mx-auto max-w-4xl pt-24">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Why Ngwees
          </p>
          <h2 className="mt-3 text-3xl font-bold text-foreground font-display sm:text-4xl">
            Built for Zambian borrowers
          </h2>
        </div>

        <div className="flex flex-wrap justify-center gap-3">
          {highlights.map((h) => (
            <div
              key={h.text}
              className="flex items-center gap-2.5 rounded-xl border border-border/50 bg-white px-5 py-3 shadow-sm"
            >
              <h.icon className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">{h.text}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 text-center">
          <Button asChild variant="ghost" className="text-primary">
            <Link to="/why-us">
              Learn more about us
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ── Testimonials (preview) ── */}
      <section className="mx-auto max-w-4xl pt-24">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Real stories
          </p>
          <h2 className="mt-3 text-3xl font-bold text-foreground font-display sm:text-4xl">
            Hear from our borrowers
          </h2>
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          {testimonials.map((t) => (
            <div
              key={t.name}
              className="rounded-2xl border border-border/40 bg-white p-6 shadow-sm"
            >
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4 fill-amber-400 text-amber-400"
                  />
                ))}
              </div>
              <p className="text-sm leading-relaxed text-foreground/85">
                "{t.text}"
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 text-center">
          <Button asChild variant="ghost" className="text-primary">
            <Link to="/stories">
              Read more stories
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="mx-auto max-w-4xl pt-24">
        <div className="rounded-3xl bg-foreground px-8 py-14 text-center text-background shadow-2xl">
          <h2 className="text-3xl font-bold font-display sm:text-4xl">
            Ready to get started?
          </h2>
          <p className="mx-auto mt-3 max-w-md text-base text-background/70">
            Apply in under 5 minutes. No paperwork, no branch visits.
            Everything happens right here.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="h-13 rounded-xl bg-white px-8 text-base text-foreground shadow-lg hover:bg-white/90"
            >
              <Link to="/register">
                Create your account
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="h-13 rounded-xl border-white/25 bg-transparent px-8 text-base text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/login">Sign in</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
